import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const FeaturesPage = () => {
    const features = [
        {
            category: "Quoting & Sales",
            items: [
                {
                    title: "One-Click Quote Generation",
                    description: "Create professional quotes in minutes. Save frequently used sections as templates.",
                    icon: "⚡"
                },
                {
                    title: "Public Quote Sharing",
                    description: "Send quotes via a secure link. Clients can view, approve, and sign digitally.",
                    icon: "🔗"
                },
                {
                    title: "Client & Opportunity Pipeline",
                    description: "Visual sales pipeline to track leads, pending quotes, and accepted jobs.",
                    icon: "📊"
                },
                {
                    title: "Quote Templates",
                    description: "Reusable templates for common jobs. Stop rewriting the same descriptions.",
                    icon: "📝"
                }
            ]
        },
        {
            category: "Invoicing & Payments",
            items: [
                {
                    title: "Quote-to-Invoice Conversion",
                    description: "Convert accepted quotes to invoices with one click. No double entry.",
                    icon: "🔄"
                },
                {
                    title: "Payment Tracking",
                    description: "Track paid and unpaid invoices. Know exactly who owes you money.",
                    icon: "💰"
                },
                {
                    title: "Public Invoice Links",
                    description: "Share invoices publicly so clients can review and process payment.",
                    icon: "🌐"
                }
            ]
        },
        {
            category: "Business Management",
            items: [
                {
                    title: "Appointment Scheduling",
                    description: "Manage your calendar and appointments. Avoid double-booking.",
                    icon: "📅"
                },
                {
                    title: "Public Booking Form",
                    description: "Let clients book appointments directly from your website or social media.",
                    icon: "👋"
                },
                {
                    title: "Dashboard & Metrics",
                    description: "Real-time overview of your business health, revenue, and pending items.",
                    icon: "📈"
                },
                {
                    title: "Email Automation",
                    description: "Automated notifications for quotes, invoices, and payments.",
                    icon: "📧"
                },
                {
                    title: "Gmail Integration",
                    description: "Send emails from your own Gmail account for a professional look.",
                    icon: "📨"
                }
            ]
        }
    ];

    return (
        <div className="min-h-screen bg-gray-50 pt-20 pb-12">
            <div className="container mx-auto px-4">
                <div className="text-center mb-16">
                    <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                        Everything You Need to Run Your Business
                    </h1>
                    <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                        SwiftBidPro consolidates quoting, invoicing, scheduling, and client management into one simple platform.
                    </p>
                </div>

                <div className="space-y-16">
                    {features.map((section, idx) => (
                        <div key={idx}>
                            <h2 className="text-2xl font-bold text-gray-900 mb-8 border-b pb-2">
                                {section.category}
                            </h2>
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                                {section.items.map((feature, fIdx) => (
                                    <motion.div
                                        key={fIdx}
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        transition={{ delay: fIdx * 0.1 }}
                                        viewport={{ once: true }}
                                        className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow border border-gray-100"
                                    >
                                        <div className="text-4xl mb-4">{feature.icon}</div>
                                        <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                                        <p className="text-gray-600">{feature.description}</p>
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="mt-20 text-center">
                    <h2 className="text-3xl font-bold text-gray-900 mb-6">Ready to get started?</h2>
                    <Link to="/register">
                        <button className="bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-700 transition-colors shadow-lg hover:shadow-xl transform hover:-translate-y-1">
                            Start Your Free Trial
                        </button>
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default FeaturesPage;
